import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import { usePatrolSteelVersionsApi } from "../../../../../http/patrol/useSteelVersion";
import { AdminCommonSteelVersion, InsertCommonSteelVersionBody } from "../../../../../http/types/commonSteelVersions";
import { useSteelVersionsApi } from "../../../../../http/useSteelVersions";
import { PaginationQuery } from "../../../../../types";
import Button from "../../../../ui/Button";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import MessageBox from "../../../../ui/MessageBox";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import DeleteSteelVersionDialog from "../../../globalSettings/steelVersions/DeleteSteelVersionDialog";
import SteelVersionDetails, { UpsertSteelVersionForm } from "../../../globalSettings/steelVersions/SteelVersionDetails";
import styles from "./SteelVersions.module.css";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const SteelVersions: FC<Props> = ({ active }) => {
  const { systemId } = useParams<{ systemId: string }>();
  const i18n = useI18n();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "patrolSteelVersions" });
  const aborter = useRef(new AbortController());
  const patrolSteelVersionsApi = usePatrolSteelVersionsApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await patrolSteelVersionsApi.updateAdminSteelVersionVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (steelVersionId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await patrolSteelVersionsApi.updateAdminSteelVersionOrder(steelVersionId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const steelVersionsApi = useSteelVersionsApi();
  const api = useApi();

  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<AdminCommonSteelVersion>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminCommonSteelVersion>("patrol.steel_version");
  const [isNewItemSubmitting, setIsNewItemSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "patrolSteelVersions");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = patrolSteelVersionsApi.useAdminMaterials(query, aborter.current.signal);

  const onSubmit = async (formData: UpsertSteelVersionForm, newImage: File | undefined) => {
    const body: InsertCommonSteelVersionBody = {
      name: formData.name!,
      nameDe: formData.nameDe,
      nameEs: formData.nameEs,
      nameFr: formData.nameFr,
      nameIt: formData.nameIt,
      order: formData.order!,
      visibility: formData.visibility,
      description: formData.description!,
      descriptionDe: formData.descriptionDe,
      descriptionIt: formData.descriptionIt,
      descriptionEs: formData.descriptionEs,
      descriptionFr: formData.descriptionFr,
      image: newImage?.name ?? undefined,
    };

    setIsNewItemSubmitting(true);

    try {
      const response = await patrolSteelVersionsApi.insertAdminMaterial(body);
      if (newImage) {
        const signedUrl = await steelVersionsApi.generateUploadImageUrl(response.id, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsNewItemSubmitting(false);
          return;
        }
      }

      setIsNewItemSubmitting(false);
      navigate(`/admin/systems/${systemId}/patrol_steel_versions/${response.id}`);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsNewItemSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.steelVersions.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.steelVersions.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminCommonSteelVersion>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/patrol_steel_versions/${item.id}`}
              >
                {item.name}
              </Link>
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => navigate(`/admin/systems/${systemId}/patrol_steel_versions/${item.id}`)}
        rowActions={(item) => [
          getEditAction(i18n, () => navigate(`/admin/systems/${systemId}/patrol_steel_versions/${item.id}`)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {showNewItemDialog && (
        <ModalDialog
          onClose={() => setShowNewItemDialog(false)}
          title={i18n.translation.steelVersions.createNew}
          fitContent
        >
          <FlexContent direction="row">
            <SteelVersionDetails onSubmit={onSubmit} isGlobalSettings={false} isSubmitting={isNewItemSubmitting} />
          </FlexContent>
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <DeleteSteelVersionDialog
          isOpen={!!itemToDelete}
          itemIdToDelete={itemToDelete?.id ?? ""}
          onClose={() => setItemToDelete(undefined)}
        >
          <div className={styles.container}>
            <MessageBox type="error" text={i18n.translation.steelVersions.warnings.deleteWarning} />
            {i18n.translation.steelVersions.questions.delete}
          </div>
        </DeleteSteelVersionDialog>
      )}
    </FlexContent>
  );
};

export default SteelVersions;
